/**
 * Browser copy of api/src/medbin/xbin.ts.
 * Uses the platform DOMParser. Keep the field order in step with the API copy.
 */

import type { MedbinKind, MedbinParty, XbinBuildInput, XbinDocument, XbinObject } from "./types";

export const XBIN_NS: Record<MedbinKind, string> = {
  XBIN01: "http://rep.oio.dk/medcom.dk/xml/schemas/2012/03/28/",
  XBIN02: "http://rep.oio.dk/medcom.dk/xml/schemas/2016/10/02/",
};

const VERSION_CODE: Record<MedbinKind, string> = {
  XBIN01: "XB0131X",
  XBIN02: "XB0210X",
};

const STATISTICAL_CODE: Record<MedbinKind, string> = {
  XBIN01: "XBIN01",
  XBIN02: "XBIN02",
};

/** Body element under Emessage. XBIN01 (2012/03/28) BinaryLetter, XBIN02 (2016/10/02) ReferralAttachment. */
export const XBIN_BODY: Record<MedbinKind, "BinaryLetter" | "ReferralAttachment"> = {
  XBIN01: "BinaryLetter",
  XBIN02: "ReferralAttachment",
};

/** A legacy XBIN02 with BinaryLetter is still read; ReferralAttachment with XBIN01 codes is refused. */
function assertBodyFitsKind(bodyName: string, kind: MedbinKind): void {
  if (bodyName === XBIN_BODY[kind]) return;
  if (kind === "XBIN02" && bodyName === "BinaryLetter") return;
  throw new Error(`${kind} body must be ${XBIN_BODY[kind]}, found ${bodyName}`);
}

export function parentUuidWithoutHyphens(objectIdentifier: string): string {
  return objectIdentifier.replaceAll("-", "");
}

export function decodedObjectSize(base64: string): number {
  const clean = base64.replace(/\s/g, "");
  if (clean.length === 0) return 0;
  const pad = clean.endsWith("==") ? 2 : clean.endsWith("=") ? 1 : 0;
  return Math.floor((clean.length * 3) / 4) - pad;
}

function assertLatin1(value: string, field: string): void {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code > 255) {
      throw new Error(`${field} is not ISO-8859-1: code point ${code}`);
    }
  }
}

function escapeText(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function optional(tag: string, value: string | undefined): string {
  if (value === undefined || value === "") return "";
  assertLatin1(value, tag);
  return `<${tag}>${escapeText(value)}</${tag}>`;
}

function required(tag: string, value: string): string {
  assertLatin1(value, tag);
  return `<${tag}>${escapeText(value)}</${tag}>`;
}

function partyXml(tag: "Sender" | "Receiver", party: MedbinParty): string {
  return [
    `<${tag}>`,
    required("EANIdentifier", party.ean),
    required("Identifier", party.identifier),
    required("IdentifierCode", party.identifierCode),
    required("OrganisationName", party.organisationName),
    optional("DepartmentName", party.departmentName),
    optional("UnitName", party.unitName),
    optional("StreetName", party.streetName),
    optional("DistrictName", party.districtName),
    optional("PostCodeIdentifier", party.postCodeIdentifier),
    tag === "Sender" ? optional("MedicalSpecialityCode", party.medicalSpecialityCode) : "",
    `</${tag}>`,
  ].filter((line) => line !== "").join("");
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

export function buildXbin(input: XbinBuildInput): string {
  const ns = XBIN_NS[input.kind];
  const base64 = bytesToBase64(input.objectBytes);
  return [
    `<?xml version="1.0" encoding="iso-8859-1"?>`,
    `<Emessage xmlns="${ns}">`,
    `<Envelope>`,
    `<Sent>`,
    required("Date", input.sentDate),
    required("Time", input.sentTime),
    `</Sent>`,
    required("Identifier", input.envelopeIdentifier),
    required("AcknowledgementCode", input.acknowledgementCode),
    `</Envelope>`,
    `<${XBIN_BODY[input.kind]}>`,
    `<Letter>`,
    required("Identifier", input.letterIdentifier),
    required("VersionCode", VERSION_CODE[input.kind]),
    required("StatisticalCode", STATISTICAL_CODE[input.kind]),
    `<Authorisation>`,
    required("Date", input.authorisationDate),
    required("Time", input.authorisationTime),
    `</Authorisation>`,
    required("TypeCode", STATISTICAL_CODE[input.kind]),
    required("StatusCode", input.statusCode),
    `</Letter>`,
    partyXml("Sender", input.sender),
    partyXml("Receiver", input.receiver),
    `<Patient>`,
    required("CivilRegistrationNumber", input.patientCpr),
    required("PersonSurnameName", input.patientSurname),
    required("PersonGivenName", input.patientGivenName),
    `</Patient>`,
    `<BinaryObject>`,
    required("ObjectIdentifier", input.objectIdentifier),
    required("ObjectCode", input.objectCode),
    required("ObjectExtensionCode", input.objectExtensionCode),
    required("OriginalObjectSize", String(input.objectBytes.byteLength)),
    required("Object_Base64Encoded", base64),
    `</BinaryObject>`,
    `</${XBIN_BODY[input.kind]}>`,
    `</Emessage>`,
  ].join("");
}

function localName(el: Element): string {
  const name = el.localName || el.nodeName;
  const idx = name.indexOf(":");
  return idx === -1 ? name : name.slice(idx + 1);
}

function childText(parent: Element, tag: string): string {
  for (let i = 0; i < parent.childNodes.length; i++) {
    const node = parent.childNodes[i];
    if (!node || node.nodeType !== 1) continue;
    const el = node as Element;
    if (localName(el) === tag) return (el.textContent ?? "").trim();
  }
  return "";
}

function childEl(parent: Element, tag: string): Element | null {
  for (let i = 0; i < parent.childNodes.length; i++) {
    const node = parent.childNodes[i];
    if (!node || node.nodeType !== 1) continue;
    const el = node as Element;
    if (localName(el) === tag) return el;
  }
  return null;
}

function requireEl(parent: Element, tag: string): Element {
  const el = childEl(parent, tag);
  if (!el) throw new Error(`XBIN missing element ${tag}`);
  return el;
}

function readParty(el: Element): MedbinParty {
  return {
    ean: childText(el, "EANIdentifier"),
    identifier: childText(el, "Identifier"),
    identifierCode: childText(el, "IdentifierCode"),
    organisationName: childText(el, "OrganisationName"),
    departmentName: childText(el, "DepartmentName") || undefined,
    unitName: childText(el, "UnitName") || undefined,
    streetName: childText(el, "StreetName") || undefined,
    districtName: childText(el, "DistrictName") || undefined,
    postCodeIdentifier: childText(el, "PostCodeIdentifier") || undefined,
    medicalSpecialityCode: childText(el, "MedicalSpecialityCode") || undefined,
  };
}

function kindFromCodes(versionCode: string, statisticalCode: string): MedbinKind {
  if (versionCode === "XB0131X" || statisticalCode === "XBIN01") return "XBIN01";
  if (versionCode === "XB0210X" || statisticalCode === "XBIN02") return "XBIN02";
  throw new Error(
    `Unsupported MEDBIN version/statistical code: ${versionCode}/${statisticalCode}`,
  );
}

export function parseXbin(xml: string): XbinDocument {
  const doc = new DOMParser().parseFromString(xml, "text/xml");
  const root = doc.documentElement;
  if (!root || localName(root) !== "Emessage") {
    throw new Error("XBIN root must be Emessage");
  }
  const envelope = requireEl(root, "Envelope");
  const sent = requireEl(envelope, "Sent");
  const letterWrap = childEl(root, "ReferralAttachment") ?? childEl(root, "BinaryLetter");
  if (!letterWrap) throw new Error("XBIN missing element ReferralAttachment or BinaryLetter");
  const bodyName = localName(letterWrap);
  const letter = requireEl(letterWrap, "Letter");
  const authorisation = requireEl(letter, "Authorisation");
  requireEl(letterWrap, "BinaryObject");
  const objects = Array.from(letterWrap.childNodes)
    .filter((n): n is Element => n.nodeType === 1 && localName(n as Element) === "BinaryObject")
    .map(readBinaryObject);
  const patient = requireEl(letterWrap, "Patient");
  const versionCode = childText(letter, "VersionCode");
  const statisticalCode = childText(letter, "StatisticalCode");
  const kind = kindFromCodes(versionCode, statisticalCode);
  assertBodyFitsKind(bodyName, kind);
  return {
    kind,
    bodyName,
    envelopeIdentifier: childText(envelope, "Identifier"),
    sentDate: childText(sent, "Date"),
    sentTime: childText(sent, "Time"),
    acknowledgementCode: childText(envelope, "AcknowledgementCode"),
    letterIdentifier: childText(letter, "Identifier"),
    authorisationDate: childText(authorisation, "Date"),
    authorisationTime: childText(authorisation, "Time"),
    statusCode: childText(letter, "StatusCode"),
    sender: readParty(requireEl(letterWrap, "Sender")),
    receiver: readParty(requireEl(letterWrap, "Receiver")),
    patientCpr: childText(patient, "CivilRegistrationNumber"),
    patientSurname: childText(patient, "PersonSurnameName"),
    patientGivenName: childText(patient, "PersonGivenName"),
    ...objects[0],
    objects,
  };
}

function readBinaryObject(binary: Element): XbinObject {
  const objectBase64 = childText(binary, "Object_Base64Encoded").replace(/\s/g, "");
  const declared = Number(childText(binary, "OriginalObjectSize"));
  return {
    objectIdentifier: childText(binary, "ObjectIdentifier"),
    objectCode: childText(binary, "ObjectCode"),
    objectExtensionCode: childText(binary, "ObjectExtensionCode"),
    originalObjectSize: declared,
    objectBase64,
    sizeMatches: declared === decodedObjectSize(objectBase64),
  };
}
